import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import SearchAdvisorSelect from "../common/SearchAdvisorSelect";
import { useManuallyAssignAdvisor } from "../../services/signup";
import { ManuallyAssignAdvisorRequest } from "../../protogen/signup_service_pb";

interface Props {
  open: boolean;
  onClose: () => void;
  submissionRef: string;
  leadName: string;
  onSuccess?: () => void;
}

export default ({
  open,
  onClose,
  submissionRef,
  leadName,
  onSuccess,
}: Props) => {
  const [selectedAdvisor, setSelectedAdvisor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { request, loading } = useManuallyAssignAdvisor();

  const handleAssign = async () => {
    if (!selectedAdvisor) {
      setError("Please select an advisor");
      return;
    }

    setError(null);

    try {
      const assignRequest = new ManuallyAssignAdvisorRequest({
        submissionRef: submissionRef,
        advisorRef: selectedAdvisor,
      });

      await request(assignRequest);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to assign advisor");
    }
  };

  const handleClose = () => {
    setSelectedAdvisor(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manually Assign Advisor</DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          Assigning an advisor will cancel all active or queued assignments for
          this family.
        </Alert>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingTop: "8px",
          }}
        >
          <Typography variant="body">
            Assign an advisor to <strong>{leadName}</strong>
          </Typography>

          <SearchAdvisorSelect
            selectedAdvisor={selectedAdvisor}
            onChange={setSelectedAdvisor}
            title="Select Advisor"
            error={error && !selectedAdvisor ? "Advisor is required" : null}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || !selectedAdvisor}
        >
          {loading ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
